import { useQuery } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { api, type DecisionSummary, type ProviderEntityView } from "@/api.js"
import { Async } from "@/components/Async.js"
import { DeclaredMetrics, DeclaredTable, Headline } from "@/components/Declared.js"
import { Notice, PageHeader, Section } from "@/components/Page.js"
import { RevokeOverride } from "@/components/RevokeOverride.js"
import { DecisionBadge, DegradedFlag, ReasonBadge } from "@/components/Status.js"
import { Badge } from "@/components/ui/badge.js"
import { Button } from "@/components/ui/button.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { useQuestions } from "@/questions.js"

/**
 * The screen that matters: somebody arrives from a apiClient's order screen with
 * a customer complaining about prepayment, and needs to know why.
 *
 * The tax id **is** the title — the system stores no name, so the identifier
 * is the entity's only name here, set in mono like every identifier.
 *
 * **Nothing here knows what a source is.** Each provider describes the entity
 * in its own terms and this renders sections; the core contributes only what is
 * genuinely its own — what we were asked, what we answered, and whether a
 * person substituted the answer.
 */
export function EntityScreen() {
  const { country = "PL", taxId = "" } = useParams()
  const { titleOf } = useQuestions()
  const state = useQuery({
    queryKey: ["entity", country, taxId],
    queryFn: () => api.entity(country, taxId),
    enabled: taxId.length > 0,
  })

  return (
    <div>
      <PageHeader
        kicker="Podmiot"
        title={`${country}${taxId}`}
        titleClassName="font-mono tracking-normal"
        actions={
          /*
           * The person who needs an override is looking at the decision it
           * will correct, so the form is one click away with this entity
           * filled in — on the Odstępstwa screen, where it will then be listed.
           */
          <Button asChild variant="outline" size="sm">
            <Link to={`/overrides?taxId=${encodeURIComponent(`${country}${taxId}`)}`}>
              Nowe odstępstwo
            </Link>
          </Button>
        }
      />

      <Async state={state} empty="Nie mamy o tym podmiocie żadnych danych.">
        {(entity) => (
          <div className="space-y-9">
            {/*
             * One notice per question an override is in force for — and the
             * revoke beside it, because the person reading this screen is the
             * one who has just found out the override is wrong, and sending
             * them to another list to undo it is a step nobody should need.
             */}
            {entity.activeOverrides.map((override) => (
              <Notice key={override.overrideId} tone="review">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                  <span>
                    <span className="font-semibold">Aktywne odstępstwo</span> ·{" "}
                    {titleOf(override.question)} —{" "}
                    {override.direction === "ALLOW" ? "odroczenie" : "przedpłata"}{" "}
                    {override.validUntil === null
                      ? "bezterminowo"
                      : `do ${formatDate(override.validUntil)}`}
                    , ustawił(a) {override.author}.
                  </span>
                  <RevokeOverride overrideId={override.overrideId} />
                </div>
              </Notice>
            ))}

            {entity.providers.map((provider) => (
              <ProviderSection key={provider.providerId} provider={provider} />
            ))}

            <Section title="Historia decyzji">
              <DecisionTable decisions={entity.decisions} showEntity={false} />
            </Section>
          </div>
        )}
      </Async>
    </div>
  )
}

function ProviderSection({ provider }: { provider: ProviderEntityView }) {
  return (
    <section className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 className="text-[15px] font-semibold text-foreground">{provider.title}</h3>
          {provider.headline !== undefined && <Headline headline={provider.headline} />}
          {!provider.covered && (
            <Badge glyph="—" title="Poza zasięgiem tego źródła, a nie awaria">
              nie obsługuje
            </Badge>
          )}
          <span className="font-mono text-xs text-muted-foreground">{provider.providerId}</span>
        </div>
        <p className="mt-1 max-w-2xl text-[13px] text-muted-foreground">{provider.summary}</p>
      </div>

      <DeclaredMetrics metrics={provider.facts} />

      {provider.tables.map((table) => (
        <DeclaredTable key={table.key} table={table} />
      ))}
    </section>
  )
}

export function DecisionTable({
  decisions,
  showEntity = true,
}: {
  decisions: readonly DecisionSummary[]
  showEntity?: boolean
}) {
  const navigate = useNavigate()
  const { titleOf } = useQuestions()

  if (decisions.length === 0) {
    return <p className="text-sm text-muted-foreground">Brak zapisanych decyzji.</p>
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Kiedy</Th>
          {showEntity && <Th>Podmiot</Th>}
          <Th>Konsument</Th>
          <Th>Pytanie</Th>
          <Th>Decyzja</Th>
          <Th>Powody</Th>
        </tr>
      </thead>
      <tbody>
        {decisions.map((decision) => (
          // The row is the link: anyone reading this table is hunting one
          // line. The timestamp stays a real link for the keyboard.
          <Tr
            key={decision.decisionId}
            className="cursor-pointer"
            onClick={() => void navigate(`/decisions/${decision.decisionId}`)}
          >
            <Td className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
              <Link
                className="no-underline hover:text-foreground"
                to={`/decisions/${decision.decisionId}`}
                onClick={(event) => event.stopPropagation()}
              >
                {formatDate(decision.evaluatedAt)}
              </Link>
            </Td>
            {showEntity && (
              <Td className="whitespace-nowrap font-mono text-[13px] font-medium">
                <Link
                  className="text-foreground no-underline hover:text-primary"
                  to={`/entities/${decision.country}/${decision.taxId}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  {decision.country}
                  {decision.taxId}
                </Link>
              </Td>
            )}
            <Td className="text-muted-foreground">{decision.apiClientId}</Td>
            <Td className="whitespace-nowrap text-muted-foreground">
              {titleOf(decision.question)}
            </Td>
            <Td className="whitespace-nowrap">
              <span className="inline-flex items-center gap-3">
                <DecisionBadge decision={decision.decision} />
                <DegradedFlag degraded={decision.degraded} />
              </span>
            </Td>
            <Td>
              <span className="flex flex-wrap gap-x-3 gap-y-0.5">
                {decision.reasonCodes.map((code) => (
                  <ReasonBadge key={code} code={code} />
                ))}
              </span>
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}

export function formatDate(value: string | null): string {
  if (value === null) return "—"
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toISOString().replace("T", " ").slice(0, 19)
}
